import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ApiCredentialEdit } from "./update";
import {
  Box,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton,
  Typography,
} from "@mui/material";
import {
  UserContainer,
  UserBox,
  UserDetailsBox,
  UserName,
  UserType,
  UserAccess,
  UserAccessDetailsBox,
  UserEditBox,
  UserEditIcon,
  UserAccessSubBox,
  MainContainer,
  UatCheckIcon,
  BatCheckIcon,
  ProdCheckIcon,
} from "./styles/index";
import {
  getApiCredentialsContact,
  putApiCredentialsContact,
  reset,
} from "../../../redux/slices/sellerDataSlice";
import { useParams } from "react-router-dom";
import { ROLES } from "../../../utils/constants";

export const ApiCredential = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const params = useParams();
  const newRef = useRef(null);

  const state = useSelector(
    ({ sellerData: { apiCredentialsContact, open }, seller: { msal } }) => ({
      apiCredentialsContact,
      open,
      msal,
    })
  );

  const [open, setOpen] = useState(state?.open);
  const [userId, setUserId] = useState(null);
  const [credentialData, setCredentialData] = useState([
    {
      credential_type_id: "G1",
      active: false,
    },
    {
      credential_type_id: "B1",
      active: false,
    },
    {
      credential_type_id: "P1",
      active: false,
    },
  ]);

  useEffect(() => {
    dispatch(getApiCredentialsContact({ sellerId: params.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        setContainerWidth(width);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "md")
  );

  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("md", "lg")
  );

  const handleUpdatePopperwidth = () => {
    if (isTablet) return containerWidth - (containerWidth / 100) * 15;

    const value = (containerWidth / 100) * 45;
    return containerWidth - value;
  };

  useEffect(() => {
    if (state?.open) {
      setOpen(null);
      dispatch(reset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.open]);

  const handleUpdateSubmit = () => {
    dispatch(
      putApiCredentialsContact({
        userId: userId,
        sellerId: params.id,
        data: credentialData,
      })
    );
  };

  const handleCredentialData = (data, userId) => {
    setCredentialData(data);
    setUserId(userId);
  };

  const handleOnClickEdit = (index) => {
    if (index === open) {
      setOpen(null);
    } else {
      setOpen(index);
    }
    // dispatch(getUserDetails({ user_id: userId, index: index }));
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleOutsideClick = (e) => {
    if (newRef.current && !newRef.current.contains(e.target)) {
      setOpen(null);
    }
  };

  const credentialOrder = { G1: 1, B1: 2, P1: 3 };
  const sortCredentials = (credentials) => {
    return credentials.sort(
      (a, b) =>
        credentialOrder[a.credential_type_id] -
        credentialOrder[b.credential_type_id]
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0px 0px 15px 0px rgba(0,0,0,0.08)",
        p: isMobile ? "24px" : "32px",
      }}
    >
      <MainContainer
        container
        theme={theme}
        width="100%"
        sx={{ overflow: "auto" }}
      >
        <Typography
          fontSize={"1rem"}
          fontWeight={600}
          color={theme.darkBlue}
          my={2}
        >
          Select which Admins should have API credentials (UAT, BAT and Prod).
        </Typography>

        <UserContainer
          item
          xs={12}
          sm={isMobile ? 12 : isTablet ? 9 : 5.9}
          ref={containerRef}
        >
          {state?.apiCredentialsContact?.user?.map((user, index) => (
            <Grid
              container
              sx={{
                position: "relative",
                ...(isMobile && {
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }),
              }}
              key={user.user_id}
            >
              <UserBox item open={open} index={index}>
                {/* <span>{index}</span> */}
                <UserDetailsBox item xs={7.8}>
                  <UserName>{`${user.user_first_name} ${user.user_last_name}`}</UserName>
                  <UserType>{user.role}</UserType>
                </UserDetailsBox>
                <UserAccessDetailsBox item xs={3.2}>
                  {user.credentials ? (
                    sortCredentials([...user.credentials]).map((cred, i) => {
                      return (
                        <UserAccessSubBox key={i}>
                          {cred?.credential_type_id === "G1" ? (
                            <UatCheckIcon />
                          ) : cred?.credential_type_id === "B1" ? (
                            <BatCheckIcon />
                          ) : (
                            <ProdCheckIcon />
                          )}
                          <UserAccess>
                            {cred?.credential_type_id === "G1"
                              ? "UAT"
                              : cred?.credential_type_id === "B1"
                              ? "BAT"
                              : "Prod"}
                          </UserAccess>
                        </UserAccessSubBox>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </UserAccessDetailsBox>
                <UserEditBox item xs={1} sm={1.7} md={1.3}>
                  {state.msal.roles.some(
                    (role) =>
                      role === ROLES.SUPER_ADMIN ||
                      role === ROLES.BUSINESS_ADMIN
                  ) ? (
                    <IconButton onClick={() => handleOnClickEdit(index)}>
                      <UserEditIcon index={index} open={open} />
                    </IconButton>
                  ) : null}
                </UserEditBox>
              </UserBox>

              {open === index ? (
                <div ref={newRef}>
                  <ApiCredentialEdit
                    width={isMobile ? "100%" : handleUpdatePopperwidth()}
                    open={open}
                    email={user.user_email}
                    credentials={user.credentials}
                    credentialData={credentialData}
                    handleUpdateSubmit={handleUpdateSubmit}
                    handleCredentialData={handleCredentialData}
                    isMobile={isMobile}
                    index={index}
                    userId={user.user_id}
                    sellerId={params.id}
                  ></ApiCredentialEdit>
                </div>
              ) : null}
            </Grid>
          ))}
        </UserContainer>

        <Box
          xs={5}
          // height={isMobile ? "0px" : `${state.userEditBoxHeight-160}px`}
        ></Box>
      </MainContainer>
    </Box>
  );
};
